import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FiAlertTriangle,
  FiFlag,
  FiGrid,
  FiMapPin,
  FiPackage,
  FiShare2,
  FiTool,
  FiTrendingUp,
  FiZap,
} from 'react-icons/fi';
import { PiTrafficSignal } from 'react-icons/pi';
import { API_ENDPOINTS } from '../config/chargerApiConfig';
import { logger } from '../utils/logger';

const DEFAULT_LAYER_PREFS = {
  SCAQMDCheckbox: false,
  sraCheckbox: false,
  mhdInfrastructureCheckbox: false,
  warehouseCheckbox: false,
  intermodalTerminalCheckbox: false,
  serviceCenterCheckbox: false,
  truckParkingLocationCheckbox: false,
  pemsTrafficCheckbox: false,
  trafficMainlineCheckbox: true,
  trafficOnrampCheckbox: true,
  trafficOfframpCheckbox: true,
  disadvantagedCommunitiesCheckbox: false,
  calEnviroScreenCheckbox: false,
  calEnviroScreenMinPercentile: 75,
  circuitCapacityCheckbox: false,
  utilitiesCheckbox: false,
};

const clampCalEnviroPercentile = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_LAYER_PREFS.calEnviroScreenMinPercentile;
  return Math.max(0, Math.min(100, n));
};

const normalizeLayerPrefs = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return DEFAULT_LAYER_PREFS;
  return {
    SCAQMDCheckbox: Boolean(parsed.SCAQMDCheckbox),
    sraCheckbox: Boolean(parsed.sraCheckbox),
    mhdInfrastructureCheckbox: Boolean(parsed.mhdInfrastructureCheckbox),
    warehouseCheckbox: Boolean(parsed.warehouseCheckbox),
    intermodalTerminalCheckbox: Boolean(parsed.intermodalTerminalCheckbox),
    serviceCenterCheckbox: Boolean(parsed.serviceCenterCheckbox),
    truckParkingLocationCheckbox: Boolean(parsed.truckParkingLocationCheckbox),
    pemsTrafficCheckbox: Boolean(parsed.pemsTrafficCheckbox),
    trafficMainlineCheckbox: parsed.trafficMainlineCheckbox !== false,
    trafficOnrampCheckbox: parsed.trafficOnrampCheckbox !== false,
    trafficOfframpCheckbox: parsed.trafficOfframpCheckbox !== false,
    disadvantagedCommunitiesCheckbox: Boolean(parsed.disadvantagedCommunitiesCheckbox),
    calEnviroScreenCheckbox: Boolean(parsed.calEnviroScreenCheckbox),
    calEnviroScreenMinPercentile: clampCalEnviroPercentile(parsed.calEnviroScreenMinPercentile),
    circuitCapacityCheckbox: Boolean(parsed.circuitCapacityCheckbox),
    utilitiesCheckbox: Boolean(parsed.utilitiesCheckbox),
  };
};

const readLayerPrefs = (storageKey) => {
  if (typeof window === 'undefined') return DEFAULT_LAYER_PREFS;
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return DEFAULT_LAYER_PREFS;
    return normalizeLayerPrefs(JSON.parse(stored));
  } catch (_) {
    return DEFAULT_LAYER_PREFS;
  }
};

const authHeaders = () => ({
  Authorization: `Token ${localStorage.getItem('authToken')}`,
});

export function useChargerOverlayLayers({ mapBounds, zoomLevel, storageKey = 'sage_map_layer_prefs' }) {
  const initialLayerPrefsRef = useRef(null);
  if (!initialLayerPrefsRef.current) {
    initialLayerPrefsRef.current = readLayerPrefs(storageKey);
  }
  const initialLayerPrefs = initialLayerPrefsRef.current;

  const [SCAQMDCheckbox, setSCAQMDCheckbox] = useState(initialLayerPrefs.SCAQMDCheckbox);
  const [sraCheckbox, setSraCheckbox] = useState(initialLayerPrefs.sraCheckbox);
  const [mhdInfrastructureCheckbox, setMhdInfrastructureCheckbox] = useState(initialLayerPrefs.mhdInfrastructureCheckbox);
  const [warehouseCheckbox, setWarehouseCheckbox] = useState(initialLayerPrefs.warehouseCheckbox);
  const [intermodalTerminalCheckbox, setIntermodalTerminalCheckbox] = useState(initialLayerPrefs.intermodalTerminalCheckbox);
  const [serviceCenterCheckbox, setServiceCenterCheckbox] = useState(initialLayerPrefs.serviceCenterCheckbox);
  const [truckParkingLocationCheckbox, setTruckParkingLocationCheckbox] = useState(initialLayerPrefs.truckParkingLocationCheckbox);
  const [pemsTrafficCheckbox, setPemsTrafficCheckbox] = useState(initialLayerPrefs.pemsTrafficCheckbox);
  const [trafficMainlineCheckbox, setTrafficMainlineCheckbox] = useState(initialLayerPrefs.trafficMainlineCheckbox);
  const [trafficOnrampCheckbox, setTrafficOnrampCheckbox] = useState(initialLayerPrefs.trafficOnrampCheckbox);
  const [trafficOfframpCheckbox, setTrafficOfframpCheckbox] = useState(initialLayerPrefs.trafficOfframpCheckbox);
  const [disadvantagedCommunitiesCheckbox, setDisadvantagedCommunitiesCheckbox] = useState(initialLayerPrefs.disadvantagedCommunitiesCheckbox);
  const [calEnviroScreenCheckbox, setCalEnviroScreenCheckbox] = useState(initialLayerPrefs.calEnviroScreenCheckbox);
  const [calEnviroScreenMinPercentile, setCalEnviroScreenMinPercentile] = useState(initialLayerPrefs.calEnviroScreenMinPercentile);
  const [circuitCapacityCheckbox, setCircuitCapacityCheckbox] = useState(initialLayerPrefs.circuitCapacityCheckbox);
  const [utilitiesCheckbox, setUtilitiesCheckbox] = useState(initialLayerPrefs.utilitiesCheckbox);

  const [scaqmdJurisdiction, setScaqmdJurisdiction] = useState([]);
  const [sraData, setSraData] = useState([]);
  const [mhdInfrastructure, setMhdInfrastructure] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [disadvantagedCommunities, setDisadvantagedCommunities] = useState([]);
  const [calEnviroScreen, setCalEnviroScreen] = useState([]);
  const [truckStops, setTruckStops] = useState([]);
  const [circuitData, setCircuitData] = useState([]);
  const [circuitContours, setCircuitContours] = useState([]);
  const [trafficData, setTrafficData] = useState([]);
  const [utilitiesData, setUtilitiesData] = useState([]);

  const trafficStationFilters = useMemo(() => ({
    mainline: trafficMainlineCheckbox,
    onramp: trafficOnrampCheckbox,
    offramp: trafficOfframpCheckbox,
  }), [trafficMainlineCheckbox, trafficOnrampCheckbox, trafficOfframpCheckbox]);

  const filteredTrafficData = useMemo(() => {
    if (!Array.isArray(trafficData) || trafficData.length === 0) return [];
    const activeTypes = new Set();
    if (trafficMainlineCheckbox) activeTypes.add('ML');
    if (trafficOnrampCheckbox) activeTypes.add('OR');
    if (trafficOfframpCheckbox) activeTypes.add('FR');
    if (activeTypes.size === 0) return [];
    return trafficData.filter((point) => activeTypes.has((point.station_type || 'ML').toString().trim().toUpperCase()));
  }, [trafficData, trafficMainlineCheckbox, trafficOnrampCheckbox, trafficOfframpCheckbox]);

  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          SCAQMDCheckbox,
          sraCheckbox,
          mhdInfrastructureCheckbox,
          warehouseCheckbox,
          intermodalTerminalCheckbox,
          serviceCenterCheckbox,
          truckParkingLocationCheckbox,
          pemsTrafficCheckbox,
          trafficMainlineCheckbox,
          trafficOnrampCheckbox,
          trafficOfframpCheckbox,
          disadvantagedCommunitiesCheckbox,
          calEnviroScreenCheckbox,
          calEnviroScreenMinPercentile: clampCalEnviroPercentile(calEnviroScreenMinPercentile),
          circuitCapacityCheckbox,
          utilitiesCheckbox,
        }),
      );
    } catch (_) {
      // no-op
    }
  }, [
    storageKey,
    SCAQMDCheckbox,
    sraCheckbox,
    mhdInfrastructureCheckbox,
    warehouseCheckbox,
    intermodalTerminalCheckbox,
    serviceCenterCheckbox,
    truckParkingLocationCheckbox,
    pemsTrafficCheckbox,
    trafficMainlineCheckbox,
    trafficOnrampCheckbox,
    trafficOfframpCheckbox,
    disadvantagedCommunitiesCheckbox,
    calEnviroScreenCheckbox,
    calEnviroScreenMinPercentile,
    circuitCapacityCheckbox,
    utilitiesCheckbox,
  ]);

  const getSCAQMDJurisdiction = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await axios.get(API_ENDPOINTS.getSCAQMDJurisdiction, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      let jurisdictions = [];
      if (response.data?.scaqmd_jurisdictions?.features) {
        jurisdictions = response.data.scaqmd_jurisdictions.features;
      } else if (Array.isArray(response.data?.scaqmd_jurisdictions)) {
        jurisdictions = response.data.scaqmd_jurisdictions;
      } else if (Array.isArray(response.data)) {
        jurisdictions = response.data;
      }
      setScaqmdJurisdiction(jurisdictions);
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching SCAQMD Jurisdiction:', error);
        setScaqmdJurisdiction([]);
      }
    }
    return controller;
  }, []);

  const getMhdInfrastructure = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await axios.get(API_ENDPOINTS.getMdhdInfrastructure, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      setMhdInfrastructure(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching MHD Infrastructure:', error);
        setMhdInfrastructure([]);
      }
    }
    return controller;
  }, []);

  const getWarehouses = useCallback(async (bounds = null, zoom = 10) => {
    const controller = new AbortController();
    try {
      const params = new URLSearchParams();
      if (bounds) params.append('bounds', `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`);
      params.append('zoom', zoom.toString());
      const response = await axios.get(`${API_ENDPOINTS.getWarehouses}?${params.toString()}`, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      let next = [];
      if (response.data?.warehouses?.features) next = response.data.warehouses.features;
      else if (Array.isArray(response.data?.warehouses)) next = response.data.warehouses;
      else if (Array.isArray(response.data)) next = response.data;
      setWarehouses(next);
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching Warehouses:', error);
        setWarehouses([]);
      }
    }
    return controller;
  }, []);

  const getDisadvantagedCommunities = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await axios.get(API_ENDPOINTS.getDisadvantagedCommunitiesConsolidated, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      let communities = [];
      if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
        communities = response.data.features;
      } else if (response.data?.disadvantaged_communities?.features) {
        communities = response.data.disadvantaged_communities.features;
      } else if (Array.isArray(response.data?.disadvantaged_communities)) {
        communities = response.data.disadvantaged_communities;
      } else if (Array.isArray(response.data)) {
        communities = response.data;
      }
      setDisadvantagedCommunities(communities);
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching Disadvantaged Communities:', error);
        setDisadvantagedCommunities([]);
      }
    }
    return controller;
  }, []);

  const getSRAData = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await axios.get(API_ENDPOINTS.getSCAQMDSourceReceptorAreas, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      const data = response.data;
      setSraData(Array.isArray(data) ? data : (data?.features ?? []));
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching SRA data:', error);
        setSraData([]);
      }
    }
    return controller;
  }, []);

  const getCalEnviroScreen = useCallback(async () => {
    const controller = new AbortController();
    const normalizedPercentile = clampCalEnviroPercentile(calEnviroScreenMinPercentile);
    try {
      const response = await axios.get(API_ENDPOINTS.getCalEnviroScreen40, {
        headers: authHeaders(),
        params: { min_ciscorep: normalizedPercentile },
        signal: controller.signal,
      });
      let features = [];
      if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
        features = response.data.features;
      } else if (Array.isArray(response.data)) {
        features = response.data;
      }
      setCalEnviroScreen(features);
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching CalEnviroScreen 4.0:', error);
        setCalEnviroScreen([]);
      }
    }
    return controller;
  }, [calEnviroScreenMinPercentile]);

  const getTruckStops = useCallback(async () => {
    const controller = new AbortController();
    try {
      const [intermodalTerminalsResponse, serviceCentersResponse] = await Promise.all([
        axios.get(API_ENDPOINTS.getIntermodalTerminals, { headers: authHeaders(), signal: controller.signal }),
        axios.get(API_ENDPOINTS.getServiceCenters, { headers: authHeaders(), signal: controller.signal }),
      ]);
      const intermodalTerminals = intermodalTerminalsResponse.data.map((terminal) => ({ ...terminal, type: 'intermodal_terminal' }));
      const serviceCenters = serviceCentersResponse.data.map((center) => ({ ...center, type: 'service_center' }));
      setTruckStops(intermodalTerminals.concat(serviceCenters));
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching Truck Stops:', error);
        setTruckStops([]);
      }
    }
    return controller;
  }, []);

  const getCircuitData = useCallback(async (bounds = null, zoom = 10) => {
    const controller = new AbortController();
    const ZOOM_THRESHOLD = 13;
    try {
      const params = new URLSearchParams();
      if (bounds) params.append('bounds', `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`);
      params.append('zoom', zoom.toString());

      if (zoom > ZOOM_THRESHOLD) {
        const response = await axios.get(`${API_ENDPOINTS.getCircuits}?${params.toString()}`, {
          headers: authHeaders(),
          signal: controller.signal,
        });
        let circuits = [];
        let circuitsLadwp = [];
        if (response.data?.circuits?.features) circuits = response.data.circuits.features;
        else if (Array.isArray(response.data?.circuits)) circuits = response.data.circuits;
        if (response.data?.circuits_ladwp?.features) circuitsLadwp = response.data.circuits_ladwp.features;
        else if (Array.isArray(response.data?.circuits_ladwp)) circuitsLadwp = response.data.circuits_ladwp;
        setCircuitData([
          ...circuits.map((circuit) => ({ ...circuit, type: 'regular' })),
          ...circuitsLadwp.map((circuit) => ({ ...circuit, type: 'ladwp' })),
        ]);
        setCircuitContours([]);
      } else {
        const response = await axios.get(`${API_ENDPOINTS.getCircuitContours}?${params.toString()}`, {
          headers: authHeaders(),
          signal: controller.signal,
        });
        let contours = [];
        if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
          contours = response.data.features;
        } else if (Array.isArray(response.data?.features)) {
          contours = response.data.features;
        } else if (Array.isArray(response.data)) {
          contours = response.data;
        }
        setCircuitContours(contours);
        setCircuitData([]);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        logger.error('Error fetching Circuit Data:', error);
        setCircuitData([]);
        setCircuitContours([]);
      }
    }
    return controller;
  }, []);

  const getTrafficData = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await axios.get(API_ENDPOINTS.getTrafficData, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      const normalized = Array.isArray(response.data)
        ? response.data.map((point) => ({
          ...point,
          station_type: (point.station_type || 'ML').toString().trim().toUpperCase(),
          truck_volume: point.truck_volume ?? point.volume ?? null,
          vehicle_volume: point.vehicle_volume ?? null,
        }))
        : [];
      setTrafficData(normalized);
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching Traffic Data:', error);
        setTrafficData([]);
      }
    }
    return controller;
  }, []);

  const getUtilitiesData = useCallback(async () => {
    const controller = new AbortController();
    try {
      const response = await axios.get(API_ENDPOINTS.getUtilityLoadmap, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      setUtilitiesData(response.data || []);
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error fetching Utilities Data:', error);
        setUtilitiesData([]);
      }
    }
    return controller;
  }, []);

  useEffect(() => {
    if (!warehouseCheckbox) return undefined;
    getWarehouses(mapBounds, zoomLevel);
    return undefined;
  }, [warehouseCheckbox, mapBounds, zoomLevel, getWarehouses]);

  useEffect(() => {
    if (!mhdInfrastructureCheckbox) return undefined;
    getMhdInfrastructure();
    return undefined;
  }, [mhdInfrastructureCheckbox, getMhdInfrastructure]);

  useEffect(() => {
    if (!SCAQMDCheckbox) return undefined;
    getSCAQMDJurisdiction();
    return undefined;
  }, [SCAQMDCheckbox, getSCAQMDJurisdiction]);

  useEffect(() => {
    if (!sraCheckbox) return undefined;
    getSRAData();
    return undefined;
  }, [sraCheckbox, getSRAData]);

  useEffect(() => {
    if (!disadvantagedCommunitiesCheckbox) return undefined;
    getDisadvantagedCommunities();
    return undefined;
  }, [disadvantagedCommunitiesCheckbox, getDisadvantagedCommunities]);

  useEffect(() => {
    if (!calEnviroScreenCheckbox) return undefined;
    getCalEnviroScreen();
    return undefined;
  }, [calEnviroScreenCheckbox, getCalEnviroScreen, calEnviroScreenMinPercentile]);

  useEffect(() => {
    if (!(intermodalTerminalCheckbox || serviceCenterCheckbox || truckParkingLocationCheckbox)) return undefined;
    getTruckStops();
    return undefined;
  }, [intermodalTerminalCheckbox, serviceCenterCheckbox, truckParkingLocationCheckbox, getTruckStops]);

  useEffect(() => {
    if (!circuitCapacityCheckbox) return undefined;
    getCircuitData(mapBounds, zoomLevel);
    return undefined;
  }, [circuitCapacityCheckbox, mapBounds, zoomLevel, getCircuitData]);

  useEffect(() => {
    if (!pemsTrafficCheckbox) return undefined;
    getTrafficData();
    return undefined;
  }, [pemsTrafficCheckbox, getTrafficData]);

  useEffect(() => {
    if (!utilitiesCheckbox) return undefined;
    getUtilitiesData();
    return undefined;
  }, [utilitiesCheckbox, getUtilitiesData]);

  const dataLayerGroups = useMemo(() => [
    {
      title: 'Charging Infrastructure',
      items: [{
        id: 'mhdInfrastructure',
        label: 'Charging Stations',
        description: 'MDHD charging stations that are open, under development, or planned (Source: CalSTART).',
        checked: mhdInfrastructureCheckbox,
        onChange: setMhdInfrastructureCheckbox,
        icon: FiZap,
        iconClass: 'text-amber-500',
      }],
    },
    {
      title: 'Trucking Infrastructure',
      items: [
        {
          id: 'warehouses',
          label: 'Warehouses',
          description: 'Warehouse footprint across Southern California (Warehouse CITY).',
          checked: warehouseCheckbox,
          onChange: setWarehouseCheckbox,
          icon: FiPackage,
          iconClass: 'text-teal-600',
        },
        {
          id: 'intermodalTerminals',
          label: 'Intermodal Terminals',
          description: 'Ocean ports, rail terminals, and equipment depots (LoadMatch).',
          checked: intermodalTerminalCheckbox,
          onChange: setIntermodalTerminalCheckbox,
          icon: FiShare2,
          iconClass: 'text-indigo-500',
        },
        {
          id: 'serviceCenters',
          label: 'Service Centers',
          description: 'Service centers providing fueling, maintenance, and rest options for trucks.',
          checked: serviceCenterCheckbox,
          onChange: setServiceCenterCheckbox,
          icon: FiTool,
          iconClass: 'text-gray-600',
        },
      ],
    },
    {
      title: 'Truck Activity',
      items: [
        {
          id: 'truckHotspots',
          label: 'Truck Hotspots',
          description: 'Aggregated locations where trucks park for extended periods.',
          checked: truckParkingLocationCheckbox,
          onChange: setTruckParkingLocationCheckbox,
          icon: FiMapPin,
          iconClass: 'text-rose-500',
        },
        {
          id: 'traffic',
          label: 'Traffic Volume',
          description: 'PeMS mainline truck volumes and on/off-ramp vehicle volumes.',
          checked: pemsTrafficCheckbox,
          onChange: setPemsTrafficCheckbox,
          icon: PiTrafficSignal,
          iconClass: 'text-emerald-600',
          children: [
            {
              id: 'trafficMainline',
              label: 'Mainline (ML)',
              checked: trafficMainlineCheckbox,
              onChange: setTrafficMainlineCheckbox,
            },
            {
              id: 'trafficOnramp',
              label: 'On-ramp (OR)',
              checked: trafficOnrampCheckbox,
              onChange: setTrafficOnrampCheckbox,
            },
            {
              id: 'trafficOfframp',
              label: 'Off-ramp (FR)',
              checked: trafficOfframpCheckbox,
              onChange: setTrafficOfframpCheckbox,
            },
          ],
        },
      ],
    },
    {
      title: 'Power Grid Information',
      items: [
        {
          id: 'gridCapacity',
          label: 'Power Grid Capacity',
          description: 'Circuit hosting capacity data from SCE and LADWP.',
          checked: circuitCapacityCheckbox,
          onChange: setCircuitCapacityCheckbox,
          icon: FiTrendingUp,
          iconClass: 'text-purple-600',
        },
        {
          id: 'utilities',
          label: 'Utilities',
          description: 'Utility service territories.',
          checked: utilitiesCheckbox,
          onChange: setUtilitiesCheckbox,
          icon: FiGrid,
          iconClass: 'text-blue-600',
        },
      ],
    },
    {
      title: 'Regulatory Boundaries',
      items: [
        {
          id: 'scaqmd',
          label: 'SCAQMD Jurisdiction',
          description: 'Projects inside SCAQMD may qualify for incentive programs.',
          checked: SCAQMDCheckbox,
          onChange: setSCAQMDCheckbox,
          icon: FiFlag,
          iconClass: 'text-orange-500',
        },
        {
          id: 'overburdened',
          label: 'EPA CEJST Disadvantaged Communities',
          description: 'Communities defined by the Inflation Reduction Act as disadvantaged.',
          checked: disadvantagedCommunitiesCheckbox,
          onChange: setDisadvantagedCommunitiesCheckbox,
          icon: FiAlertTriangle,
          iconClass: 'text-red-500',
        },
        {
          id: 'sra',
          label: 'SCAQMD Source Receptor Areas',
          description: 'Source receptor areas with MONITORING classification.',
          checked: sraCheckbox,
          onChange: setSraCheckbox,
          icon: FiMapPin,
          iconClass: 'text-cyan-600',
        },
        {
          id: 'calenviroscreen40',
          label: 'CalEnviroScreen 4.0',
          description: 'California census tracts ranked by cumulative pollution and socioeconomic burden (OEHHA).',
          checked: calEnviroScreenCheckbox,
          onChange: setCalEnviroScreenCheckbox,
          numericFilter: {
            label: 'Percentile',
            value: calEnviroScreenMinPercentile,
            min: 0,
            max: 100,
            step: 1,
            onChange: (nextValue) => {
              const parsed = Number(nextValue);
              if (!Number.isFinite(parsed)) {
                setCalEnviroScreenMinPercentile(75);
                return;
              }
              setCalEnviroScreenMinPercentile(Math.max(0, Math.min(100, parsed)));
            },
          },
          icon: FiAlertTriangle,
          iconClass: 'text-amber-600',
        },
      ],
    },
  ], [
    mhdInfrastructureCheckbox,
    warehouseCheckbox,
    intermodalTerminalCheckbox,
    serviceCenterCheckbox,
    truckParkingLocationCheckbox,
    pemsTrafficCheckbox,
    trafficMainlineCheckbox,
    trafficOnrampCheckbox,
    trafficOfframpCheckbox,
    SCAQMDCheckbox,
    sraCheckbox,
    disadvantagedCommunitiesCheckbox,
    calEnviroScreenCheckbox,
    calEnviroScreenMinPercentile,
    circuitCapacityCheckbox,
    utilitiesCheckbox,
  ]);

  const activeDataLayerCount = useMemo(
    () => dataLayerGroups.reduce((sum, group) => sum + group.items.filter((item) => item.checked).length, 0),
    [dataLayerGroups],
  );

  return {
    activeDataLayerCount,
    dataLayerGroups,
    layerData: {
      scaqmdJurisdiction,
      sraData,
      mhdInfrastructure,
      warehouses,
      disadvantagedCommunities,
      calEnviroScreen,
      truckStops,
      circuitData,
      circuitContours,
      trafficData: filteredTrafficData,
      trafficStationFilters,
      utilitiesData,
    },
    layerVisibility: {
      landParcelCheckbox: false,
      SCAQMDCheckbox,
      sraCheckbox,
      mhdInfrastructureCheckbox,
      warehouseCheckbox,
      truckStopCheckbox: false,
      intermodalTerminalCheckbox,
      serviceCenterCheckbox,
      truckParkingLocationCheckbox,
      pemsTrafficCheckbox,
      disadvantagedCommunitiesCheckbox,
      calEnviroScreenCheckbox,
      circuitCapacityCheckbox,
      utilitiesCheckbox,
    },
  };
}
